<template>
  <a :href="normalizedHash" @click="onClick">
    <slot />
  </a>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useReducedMotion } from '@/composables/useReducedMotion';
import { useLocale } from '@/composables/useLocale';

interface Props {
  /** Hash-якорь, `#id` или просто `id`. */
  hash: string;
  /** Дополнительный offset для sticky-хедера (px). */
  offset?: number;
}

const props = withDefaults(defineProps<Props>(), {
  offset: 80,
});

const emit = defineEmits<{
  (e: 'navigate', hash: string): void;
}>();

const router = useRouter();
const route = useRoute();
const reduced = useReducedMotion();
const { locale } = useLocale();

const normalizedHash = computed(() => (props.hash.startsWith('#') ? props.hash : `#${props.hash}`));
const homeTo = computed(() => `/${locale.value}/`);
const isOnHome = computed(() => route.name === 'home' || route.path === homeTo.value);

/**
 * Плавный скролл до элемента + router.replace(hash), чтобы URL обновился без
 * перезагрузки и работал back/forward. Фокус переводится на target для
 * screen-readers (если элемент фокусабелен или получит tabindex=-1).
 */
function scrollToTarget(): void {
  if (typeof document === 'undefined') return;
  const id = normalizedHash.value.slice(1);
  const target = document.getElementById(id);
  if (!target) return;
  const rect = target.getBoundingClientRect();
  const top = rect.top + (window.scrollY || 0) - props.offset;
  window.scrollTo({ top, behavior: reduced.value ? 'auto' : 'smooth' });

  const prevTabindex = target.getAttribute('tabindex');
  if (prevTabindex === null) target.setAttribute('tabindex', '-1');
  target.focus({ preventScroll: true });
  if (prevTabindex === null) {
    const cleanup = (): void => target.removeAttribute('tabindex');
    target.addEventListener('blur', cleanup, { once: true });
  }
}

async function onClick(event: MouseEvent): Promise<void> {
  if (event.defaultPrevented) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (event.button !== 0) return;
  event.preventDefault();
  if (isOnHome.value) {
    await router.replace({ path: route.path, hash: normalizedHash.value });
    scrollToTarget();
  } else {
    // На подстранице сначала уходим на главную, потом router
    // отработает scrollBehavior по `to.hash` — ручной scroll здесь
    // сработал бы до навигации и промахнулся.
    await router.push({ path: homeTo.value, hash: normalizedHash.value });
  }
  emit('navigate', normalizedHash.value);
}
</script>
